import { useRef, useLayoutEffect } from "react";
import type { ArticleEditorState, ArticleEditorActions } from "../../hooks/useArticleEditor";
import { CategoryDropdown } from "./CategoryDropdown";
import { DurationDropdown } from "./DurationDropdown";
import { CoverUpload } from "./CoverUpload";
import { BlockPicker } from "./BlockPicker";
import { HeadingBlock } from "./HeadingBlock";
import { TextBlock } from "./TextBlock";
import { ButtonBlock } from "./ButtonBlock";
import { ButtonSettingsPanel } from "./ButtonSettingsPanel";
import { Toast } from "@hiya/ui";
import iconChevronLeft from "../../assets/icon-chevron-left.svg";

interface ArticleEditorFormProps {
  title: string;
  toast: { message: string; visible: boolean };
  state: ArticleEditorState;
  actions: ArticleEditorActions;
  onBack: () => void;
  onPublish: () => void;
}

export function ArticleEditorForm({
  title,
  toast,
  state,
  actions,
  onBack,
  onPublish,
}: ArticleEditorFormProps) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.style.height = "auto";
      bodyRef.current.style.height = `${bodyRef.current.scrollHeight}px`;
    }
  }, [state.bodyText]);

  const buttonPanelBlock = state.additionalBlocks.find(
    (b) => b.id === state.buttonPanelTarget,
  );

  return (
    <div className="flex flex-col min-h-[calc(100vh-110px)]">
      <Toast message={toast.message} visible={toast.visible} />

      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={onBack} className="hover:opacity-70">
          <img src={iconChevronLeft} alt="Back" width={24} height={24} />
        </button>
        <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
      </div>

      <div className="flex-1">
        <div className="flex gap-5 mb-5">
          <CategoryDropdown value={state.category} onChange={actions.setCategory} />
          <DurationDropdown value={state.duration} onChange={actions.setDuration} />
        </div>

        <div className="mb-5">
          <CoverUpload coverPreview={state.coverPreview} onFileSelect={actions.handleCoverSelect} />
        </div>

        <input
          type="text"
          value={state.heading}
          onChange={(e) => actions.setHeading(e.target.value)}
          placeholder="Heading"
          className="w-full text-2xl font-bold text-text-primary placeholder:text-text-tertiary bg-transparent border-none outline-none mb-5 leading-[1.2]"
        />

        <textarea
          ref={bodyRef}
          value={state.bodyText}
          onChange={(e) => actions.setBodyText(e.target.value)}
          placeholder="Text"
          className="w-full text-sm text-text-primary placeholder:text-text-tertiary bg-transparent border-none outline-none leading-[1.5] resize-none min-h-[120px] mb-5 overflow-hidden"
        />

        <div className="flex flex-col gap-5 mb-5">
          {state.additionalBlocks.map((block) => {
            if (block.type === "heading") {
              return (
                <HeadingBlock
                  key={block.id}
                  value={block.content}
                  onChange={(v) => actions.updateBlock(block.id, v)}
                  onDelete={() => actions.deleteBlock(block.id)}
                />
              );
            }
            if (block.type === "text") {
              return (
                <TextBlock
                  key={block.id}
                  value={block.content}
                  onChange={(v) => actions.updateBlock(block.id, v)}
                  onDelete={() => actions.deleteBlock(block.id)}
                />
              );
            }
            return (
              <ButtonBlock
                key={block.id}
                label={block.content}
                link={block.link ?? ""}
                onEdit={() => actions.setButtonPanelTarget(block.id)}
                onDelete={() => actions.deleteBlock(block.id)}
              />
            );
          })}
        </div>

        <BlockPicker onSelect={actions.addBlock} />
      </div>

      <div className="-mx-6 -mb-6 mt-6 border-t border-stroke-primary bg-bg-primary px-6 pb-6 pt-5 flex justify-end">
        <button
          type="button"
          disabled={!state.isValid || state.saving}
          onClick={onPublish}
          className={`h-[51px] px-4 rounded-2xl text-base font-semibold ${
            state.isValid && !state.saving
              ? "bg-btn-primary text-text-btn"
              : "bg-btn-primary-disabled text-text-btn cursor-not-allowed"
          }`}
        >
          {state.saving ? "Publishing..." : "Save and publish"}
        </button>
      </div>

      <ButtonSettingsPanel
        open={state.buttonPanelTarget !== null}
        initialText={buttonPanelBlock?.content ?? ""}
        initialLink={buttonPanelBlock?.link ?? ""}
        onSave={(text, link) => {
          if (state.buttonPanelTarget) actions.updateBlock(state.buttonPanelTarget, text, link);
          actions.setButtonPanelTarget(null);
        }}
        onClose={() => {
          if (state.buttonPanelTarget && !buttonPanelBlock?.content) {
            actions.deleteBlock(state.buttonPanelTarget);
          }
          actions.setButtonPanelTarget(null);
        }}
      />
    </div>
  );
}
